import { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";

type SmoothImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  /**
   * Classes for the wrapper element that hosts the shimmer placeholder.
   * Use this to position/size the image (e.g. "absolute inset-0", "h-full w-full").
   */
  wrapperClassName?: string;
};

/**
 * Image that lazy-loads and fades in smoothly once decoded, showing a subtle
 * shimmer placeholder while it loads. A drop-in replacement for <img>; the
 * caller's onError fallback still runs and simply triggers another load+fade.
 *
 * The fade (opacity + blur) is applied via inline style rather than utility
 * classes so it never conflicts with — or gets stripped by tailwind-merge
 * against — a caller's own transition/transform classes (e.g. hover zoom).
 */
export const SmoothImage = forwardRef<HTMLImageElement, SmoothImageProps>(
  ({ className, wrapperClassName, onLoad, onError, style, ...props }, ref) => {
    const [loaded, setLoaded] = useState(false);

    return (
      <span
        className={cn(
          "block overflow-hidden bg-muted/60",
          !loaded && "animate-pulse",
          wrapperClassName
        )}
      >
        <img
          ref={ref}
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e);
          }}
          onError={(e) => {
            // Treat a failed load as "settled" so an image without a working
            // fallback src doesn't stay an invisible, perpetually-pulsing box.
            setLoaded(true);
            onError?.(e);
          }}
          className={className}
          style={{
            opacity: loaded ? 1 : 0,
            // Only apply the load-in blur while loading. Once loaded we clear
            // the inline filter (rather than setting blur(0)) so a caller's
            // Tailwind filter utility — e.g. `grayscale` for unavailable
            // rooms — is not overridden by this inline style.
            filter: loaded ? undefined : "blur(12px)",
            // Include transform so a caller's hover-zoom still animates even
            // though we own the transition shorthand here.
            transition:
              "opacity 700ms ease, filter 700ms ease, transform 600ms ease",
            ...style,
          }}
          {...props}
        />
      </span>
    );
  }
);
SmoothImage.displayName = "SmoothImage";
